import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { UserState, User } from '../../types'
import * as userAPI from '../../api/users'

const initialState: UserState = {
  users: [],
  companies: [],
  departments: [],
  companyDepartments: [],
  isLoading: false,
  error: null,
}

// Async thunks
export const fetchUsers = createAsyncThunk(
  'users/fetchUsers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await userAPI.getUsers()
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch users')
    }
  }
)

export const fetchCompanies = createAsyncThunk(
  'users/fetchCompanies',
  async (_, { rejectWithValue }) => {
    try {
      const response = await userAPI.getCompanies()
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch companies')
    }
  }
)

export const fetchDepartments = createAsyncThunk(
  'users/fetchDepartments',
  async (_, { rejectWithValue }) => {
    try {
      const response = await userAPI.getDepartments()
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch departments')
    }
  }
)

export const fetchCompanyDepartments = createAsyncThunk(
  'users/fetchCompanyDepartments',
  async (_, { rejectWithValue }) => {
    try {
      const response = await userAPI.getCompanyDepartments()
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch company departments')
    }
  }
)

export const updateUserProfile = createAsyncThunk(
  'users/updateProfile',
  async (data: { first_name: string; last_name: string }, { rejectWithValue }) => {
    try {
      const response = await userAPI.updateProfile(data)
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update profile')
    }
  }
)

export const changeUserPassword = createAsyncThunk(
  'users/changePassword',
  async (
    data: { current_password: string; new_password: string; confirm_password: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await userAPI.changePassword(data)
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to change password')
    }
  }
)

const userSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    addUser: (state, action: PayloadAction<User>) => {
      state.users.push(action.payload)
    },
    updateUser: (state, action: PayloadAction<User>) => {
      const index = state.users.findIndex(user => user.id === action.payload.id)
      if (index !== -1) {
        state.users[index] = action.payload
      }
    },
    removeUser: (state, action: PayloadAction<string>) => {
      state.users = state.users.filter(user => user.id !== action.payload)
    },
  },
  extraReducers: (builder) => {
    // Fetch Users
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.isLoading = false
        state.users = action.payload
        state.error = null
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })

    // Fetch Companies
    builder
      .addCase(fetchCompanies.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchCompanies.fulfilled, (state, action) => {
        state.isLoading = false
        state.companies = action.payload
        state.error = null
      })
      .addCase(fetchCompanies.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })

    // Fetch Departments
    builder
      .addCase(fetchDepartments.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchDepartments.fulfilled, (state, action) => {
        state.isLoading = false
        state.departments = action.payload
        state.error = null
      })
      .addCase(fetchDepartments.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })

    // Fetch Company Departments
    builder
      .addCase(fetchCompanyDepartments.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchCompanyDepartments.fulfilled, (state, action) => {
        state.isLoading = false
        state.companyDepartments = action.payload
        state.error = null
      })
      .addCase(fetchCompanyDepartments.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })

    // Update Profile
    builder
      .addCase(updateUserProfile.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(updateUserProfile.fulfilled, (state) => {
        state.isLoading = false
        state.error = null
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })

    // Change Password
    builder
      .addCase(changeUserPassword.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(changeUserPassword.fulfilled, (state) => {
        state.isLoading = false
        state.error = null
      })
      .addCase(changeUserPassword.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
  },
})

export const { clearError, addUser, updateUser, removeUser } = userSlice.actions
export default userSlice.reducer
